import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';

const AdminSuratIjinPulangAwalPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });

  // Real data for early leave permits
  const [suratIjinRequests, setSuratIjinRequests] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    async function fetchPermits() {
      // Debug user state
    
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        
        // Use the imported authFetch instead of window.authFetch
        const res = await authFetch(`${apiBase}/api/admin/earlyleave-permits`, {
          headers: { 'Accept': 'application/json' }
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Failed to fetch permits: ${res.status} ${errorText}`);
        }
        
        const data = await res.json();
        
        // Handle both Laravel Resource Collection format and direct array
        const sourceData = data.data || data || [];
        
        // Map backend data to UI format for early leave permits
        const mapped = sourceData.map(item => {
          return {
            id: item.id,
            // Store both formatted date for display and ISO date for form inputs
            tanggalPengajuan: item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) : '',
            // Keep original date in ISO format for date inputs
            tanggalPengajuanISO: item.created_at ? item.created_at.slice(0,10) : '',
            // Student name field is correct
            siswaMengajukan: item.student ? item.student.name : '',
            // Student has 'class' field, not 'kelas'
            kelas: item.student ? (item.student.class || '') : '',
            nis: item.student ? (item.student.nis || '') : '',
            // Early leave specific fields
            pulangJam: item.earlyleave_time || '',
            keperluan: item.purpose || '',
            tempatTujuan: item.destination || '',
            penanggungjawab: item.pic || '',
            hubungan: item.relationship || '',
            nomorTelepon: item.pic_contact_number || '',
            // Use request_status field instead of status
            status: item.request_status || item.status || 'menunggu',
            // Add activity field
            activity: item.activity || 'Teori',
          };
        });
        setSuratIjinRequests(mapped);
      } catch (e) {
        setNotification({
          show: true,
          message: `Error fetching data: ${e.message}`,
          type: 'error'
        });
        
        // Auto-hide error notification after 5 seconds
        setTimeout(() => {
          setNotification({ show: false, message: '', type: 'success' });
        }, 5000);
        
        setSuratIjinRequests([]);
      }
    }
    fetchPermits();
    
    // eslint-disable-next-line
  }, [user]);

  const filteredRequests = suratIjinRequests.filter(request => {
    const q = searchQuery.toLowerCase();
    return (
      (request.siswaMengajukan && request.siswaMengajukan.toLowerCase().includes(q)) ||
      (request.kelas && request.kelas.toLowerCase().includes(q)) ||
      (request.nis && request.nis.toLowerCase().includes(q)) ||
      (request.penanggungjawab && request.penanggungjawab.toLowerCase().includes(q)) ||
      (request.keperluan && request.keperluan.toLowerCase().includes(q)) ||
      (request.tempatTujuan && request.tempatTujuan.toLowerCase().includes(q))
    );
  });

  // Pagination logic
  const totalItems = filteredRequests.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredRequests.slice(startIndex, endIndex);

  // Reset current page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Fetch user data from localStorage for sidebar
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <PageContainer>
      
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Surat Ijin Pulang Awal</h1>
            <Subtitle>Kelola dan tinjau permintaan surat ijin pulang awal</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <SearchContainer>
                <SearchIcon>🔍</SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Cari berdasarkan nama guru, siswa, atau mata pelajaran..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </SearchContainer>
            </ToolbarSection>

            <TableContainer>
              {notification.show && (
                <NotificationToast type={notification.type}>
                  {notification.message}
                </NotificationToast>
              )}
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal Pengajuan</th>
                    <th>Siswa</th>
                    <th>Kelas</th>
                    <th>Pulang Jam</th>
                    <th>Keperluan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {currentPageItems.length > 0 ? (
                    currentPageItems.map((request) => (
                      <TableRow
                        key={request.id}
                        onClick={() => setSelectedPermit(request)}
                        selected={selectedPermit?.id === request.id}
                      >
                        <td>{request.tanggalPengajuan}</td>
                        <td>{request.siswaMengajukan}</td>
                        <td>{request.kelas}</td>

                        <td>{request.pulangJam}</td>
                        <td>{request.keperluan}</td>

                        <td>
                          <StatusBadge status={request.status}>
                            {request.status === 'pending' || request.status === 'menunggu' ? 'Menunggu' :
                              request.status === 'approved' || request.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPermit(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada permohonan ijin
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalItems > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} item
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {(() => {
                      const maxVisiblePages = 5;
                      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      const pages = [];
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <PageNumber
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            active={currentPage === i}
                          >
                            {i}
                          </PageNumber>
                        );
                      }
                      return pages;
                    })()}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedPermit && (
        <DetailModal onClick={() => setSelectedPermit(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <SuratIjinEditModal
              selectedPermit={selectedPermit}
              setSelectedPermit={setSelectedPermit}
            />
          </DetailCard>
        </DetailModal>
      )}
    </PageContainer>
  );
};

// Reuse the same styled components from AdminKonselingPage
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

// Activity toggle button styles
const TypeToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: 1 / -1;
`;

const TypeToggleLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const TypeToggleButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TypeToggleButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid ${props => props.active ? '#007bff' : '#e0e0e0'};
  background-color: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #007bff;
    background-color: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

// Editable modal for Surat Ijin Pulang Awal
function SuratIjinEditModal({ selectedPermit, setSelectedPermit }) {
  const [editPermit, setEditPermit] = React.useState(selectedPermit);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  React.useEffect(() => {
    setEditPermit(selectedPermit);
  }, [selectedPermit]);

  // Function to handle activity change
  const handleActivityChange = (activity) => {
    setEditPermit({ ...editPermit, activity });
  };

  // Function to validate time format (HH:MM)
  const isValidTimeFormat = (timeString) => {
    if (!timeString || timeString.trim() === '') return false;
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeString.trim());
  };

  // Function to update permit status
  const updatePermitStatus = async (status) => {
    if (!selectedPermit || !selectedPermit.id) return;
    
    setIsSubmitting(true);
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Prepare and validate data
      const requestData = {
        status,
        earlyleave_time: isValidTimeFormat(editPermit.pulangJam) ? editPermit.pulangJam.trim() : null,
        purpose: editPermit.keperluan || null,
        destination: editPermit.tempatTujuan || null,
        pic: editPermit.penanggungjawab || null,
        relationship: editPermit.hubungan || null,
        pic_contact_number: editPermit.nomorTelepon || null,
        activity: editPermit.activity || 'Teori'
      };
      
      console.log('Sending request data:', requestData);
      
      const res = await authFetch(`${apiBase}/api/admin/earlyleave-permits/${selectedPermit.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
      }
      
      const updatedPermit = await res.json();
      console.log('Status updated:', updatedPermit);
      
      // Update the local state with the new status
      const updatedLocalPermit = {
        ...editPermit,
        status: status,
        request_status: status
      };
      
      setNotification({
        show: true,
        message: `Status berhasil diupdate menjadi ${status}`,
        type: 'success'
      });
      
      // Auto-hide notification and refresh after 2 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error updating status:', error);
      setNotification({
        show: true,
        message: `Gagal mengupdate status: ${error.message}`,
        type: 'error'
      });
      
      // Auto-hide error notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form
        onSubmit={e => {
          e.preventDefault();
          // Form submission is now handled by the Terima/Tolak buttons
        }}
      >
      <DetailHeader>
        <DetailHeaderContent>
          <h2>Edit Surat Ijin Pulang Awal</h2>
          <DetailStatus status={selectedPermit.status}>
            {selectedPermit.status === 'pending' || selectedPermit.status === 'menunggu' ? 'Menunggu' :
              selectedPermit.status === 'approved' || selectedPermit.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
          </DetailStatus>
        </DetailHeaderContent>
        <CloseButton onClick={() => setSelectedPermit(null)}>×</CloseButton>
      </DetailHeader>

      <DetailContent>
        <DetailSection>
          <DetailGrid>
            <DetailGroup>
              <DetailLabel>Tanggal Pengajuan</DetailLabel>
              <input
                type="date"
                value={editPermit.tanggalPengajuanISO || ''}
                onChange={e => {
                  const newDate = e.target.value;
                  // Update both ISO and formatted date
                  setEditPermit({ 
                    ...editPermit, 
                    tanggalPengajuanISO: newDate,
                    tanggalPengajuan: newDate ? new Date(newDate).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : ''
                  });
                }}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Siswa yang mengajukan</DetailLabel>
              <input
                type="text"
                value={editPermit.siswaMengajukan}
                onChange={e => setEditPermit({ ...editPermit, siswaMengajukan: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Kelas</DetailLabel>
              <input
                type="text"
                value={editPermit.kelas}
                onChange={e => setEditPermit({ ...editPermit, kelas: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>NIS</DetailLabel>
              <input
                type="text"
                value={editPermit.nis}
                onChange={e => setEditPermit({ ...editPermit, nis: e.target.value })}
              />
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

        <DetailSection>
          {/* Add Pilih Kelas toggle buttons */}
          <TypeToggleContainer>
            <TypeToggleLabel>Pilih Kelas</TypeToggleLabel>
            <TypeToggleButtonGroup>
              <TypeToggleButton
                type="button"
                active={editPermit.activity === 'Teori'}
                onClick={() => handleActivityChange('Teori')}
              >
                Teori
              </TypeToggleButton>
              <TypeToggleButton
                type="button"
                active={editPermit.activity === 'Praktek'}
                onClick={() => handleActivityChange('Praktek')}
              >
                Praktek
              </TypeToggleButton>
            </TypeToggleButtonGroup>
          </TypeToggleContainer>
        </DetailSection>

        <DetailSection>
          <DetailGrid>
            <DetailGroup>
              <DetailLabel>Pulang Jam</DetailLabel>
              <input
                type="time"
                value={editPermit.pulangJam}
                onChange={e => setEditPermit({ ...editPermit, pulangJam: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup full>
              <DetailLabel>Ada keperluan apa</DetailLabel>
              <textarea
                value={editPermit.keperluan}
                onChange={e => setEditPermit({ ...editPermit, keperluan: e.target.value })}
                style={{ width: '100%', minHeight: 60 }}
              />
            </DetailGroup>
            <DetailGroup full>
              <DetailLabel>Nama tempat dan alamat tujuan</DetailLabel>
              <textarea
                value={editPermit.tempatTujuan}
                onChange={e => setEditPermit({ ...editPermit, tempatTujuan: e.target.value })}
                style={{ width: '100%', minHeight: 60 }}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Nama penanggungjawab</DetailLabel>
              <input
                type="text"
                value={editPermit.penanggungjawab}
                onChange={e => setEditPermit({ ...editPermit, penanggungjawab: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Hubungan</DetailLabel>
              <input
                type="text"
                value={editPermit.hubungan}
                onChange={e => setEditPermit({ ...editPermit, hubungan: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Nomor telepon penanggungjawab</DetailLabel>
              <input
                type="text"
                value={editPermit.nomorTelepon}
                onChange={e => setEditPermit({ ...editPermit, nomorTelepon: e.target.value })}
              />
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

        <ActionButtons>
        <RejectButton 
            type="button" 
            onClick={() => updatePermitStatus('ditolak')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Tolak'}
          </RejectButton>
          <ApproveButton 
            type="button" 
            onClick={() => updatePermitStatus('disetujui')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Disetujui'}
          </ApproveButton>
         
        </ActionButtons>
      </DetailContent>
    </form>
    {notification.show && (
      <NotificationToast type={notification.type}>
        {notification.message}
      </NotificationToast>
    )}
    </>
  );
}

// Pagination styled components
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: var(--background-light);
    border-color: var(--primary, #007bff);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid ${props => props.active ? 'var(--primary, #007bff)' : 'var(--border)'};
  background-color: ${props => props.active ? 'var(--primary, #007bff)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? 'var(--primary-dark, #0056b3)' : 'var(--background-light)'};
    border-color: var(--primary, #007bff);
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 'var(--danger, #c01c28)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

export default AdminSuratIjinPulangAwalPage;